import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Send, ArrowLeft, ArrowRight, Phone, Video, Info, Mic, Square, Image as ImageIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { auth } from '../../lib/firebase';
import { useConversations, useMessages } from '../../hooks/useMessaging';
import type { Conversation, Message } from '../../services/messaging';
import CampusHeader from '../../components/feature/CampusHeader';
import Footer from '../../components/feature/Footer';
import { usePresence, useSelfPresence } from '../../hooks/usePresence';
import { setTyping } from '../../services/presence';
import { subscribeAuth, getUserProfile } from '../../services/auth';
import CallModal from '../../components/calls/CallModal';
import { subscribeIncomingCalls } from '../../services/calls';
import { uploadMessageAttachment } from '../../services/storage';
import { getListingById } from '../../services/listings';
import type { Listing } from '../../services/listings';

const formatNaira = (n: number) =>
  new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }).format(n);

// Formats seconds as m:ss / 0:ss
function formatAudioDuration(seconds: number): string {
  if (!isFinite(seconds) || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Loads audio metadata and shows a mic icon + duration (used in conversation list preview)
const AudioPreview: React.FC<{ src: string }> = ({ src }) => {
  const [duration, setDuration] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!src) return;
    const audio = new Audio();
    audio.preload = 'metadata';
    const onLoaded = () => {
      const d = audio.duration;
      if (isFinite(d) && d > 0) setDuration(d);
      else setFailed(true);
    };
    const onError = () => setFailed(true);
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('error', onError);
    audio.src = src;
    return () => {
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('error', onError);
      audio.src = '';
    };
  }, [src]);
  return (
    <span className="inline-flex items-center gap-1 text-gray-600">
      <Mic size={14} />
      <span>{failed ? 'Voice note' : duration != null ? formatAudioDuration(duration) : '…'}</span>
    </span>
  );
};

const MessagesPage: React.FC = () => {
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showFraudBanner, setShowFraudBanner] = useState(true);
  const [showFraudModal, setShowFraudModal] = useState(false);
  const [pendingSendText, setPendingSendText] = useState('');
  // Call modal state
  const [showCallModal, setShowCallModal] = useState(false);
  const [callType, setCallType] = useState<'audio' | 'video'>('audio');
  const [isCaller, setIsCaller] = useState(true);
  const [activeCallId, setActiveCallId] = useState<string | undefined>(undefined);
  const [calleeIdForCall, setCalleeIdForCall] = useState<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [recording, setRecording] = useState(false);
  const [recordingLevel, setRecordingLevel] = useState(0);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const recordingIntervalRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [imagePreviewMessage, setImagePreviewMessage] = useState<Message | null>(null);
  const [showMessagesTutorial, setShowMessagesTutorial] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [otherProfile, setOtherProfile] = useState<any | null>(null);


  const [currentUser, setCurrentUser] = useState<any>(auth.currentUser);
  const [authLoading, setAuthLoading] = useState(true);
  useEffect(() => {
    const unsub = subscribeAuth(user => {
      setCurrentUser(user);
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  const { conversations, loading: conversationsLoading } = useConversations(currentUser?.uid);
  const { messages, loading: messagesLoading, sending, sendMessage, markAsRead } = useMessages(selectedConversation);

  // Determine the other participant's ID
  const otherParticipantId = selectedConversation
    ? conversations.find(c => c.id === selectedConversation)
        ?.participants.find(p => p !== currentUser?.uid) || null
    : null;

  const { presence: otherUserPresence } = usePresence(otherParticipantId);
  const { setTypingState } = useSelfPresence();

  // Subscribe for incoming calls (callee mode)
  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsubIncoming = subscribeIncomingCalls(currentUser.uid, (rec) => {
      setCallType(rec.type);
      setIsCaller(false);
      setActiveCallId(rec.id);
      setCalleeIdForCall(rec.calleeId);
      setShowCallModal(true);
    });
    return () => unsubIncoming();
  }, [currentUser?.uid]);
  useSelfPresence();

  // Show a 4s banner warning about off-platform payments
  useEffect(() => {
    setShowFraudBanner(true);
    const t = setTimeout(() => setShowFraudBanner(false), 4000);
    return () => clearTimeout(t);
  }, []);

  // Detect potential account numbers in messages and show modal
  const hasShownFraudModalRef = useRef(false);
  const containsAccountNumber = (text: string) => {
    if (!text) return false;
    const normalized = text.replace(/[^0-9]/g, '');
    if (normalized.length >= 10) return true; // 10+ digits (e.g., NG bank acct, card-like)
    const groupedCardPattern = /(?:\d{4}[ -]?){3,4}/;
    return groupedCardPattern.test(text);
  };
  const shouldWarnMessage = (text: string) => {
    const t = (text || '').toLowerCase();
    const paymentKeywords = /(pay|payment|transfer|bank|account|acct|card|bvn|iban|sort code|routing|swift|usdt|btc|bitcoin|crypto|wallet|address|digits)/i;
    const contactKeywords = /(phone|whatsapp|telegram|email|gmail|yahoo|contact|call|dm)/i;
    return containsAccountNumber(text) || paymentKeywords.test(t) || contactKeywords.test(t);
  };
  useEffect(() => {
    if (hasShownFraudModalRef.current) return;
    const recentText = messages.slice(-20).map(m => m.content).join(' ');
    if (containsAccountNumber(recentText)) {
      setShowFraudModal(true);
      hasShownFraudModalRef.current = true;
    }
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive from other users
  // or when user is already near the bottom
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    // Only auto-scroll if user is near the bottom (within 200px)
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
    
    // Only auto-scroll for new messages from other users or when user is at bottom
    const lastMessage = messages[messages.length - 1];
    const isNewMessageFromOther = lastMessage && lastMessage.senderId !== currentUser?.uid;
    
    if (isNearBottom || isNewMessageFromOther) {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } else {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [messages, currentUser?.uid]);

  // Show tutorial for first-time users
  useEffect(() => {
    try {
      const seen = localStorage.getItem('messages_tutorial_seen');
      if (!seen) setShowMessagesTutorial(true);
    } catch {}
  }, []);

  // Mark messages as read when conversation is selected
  useEffect(() => {
    if (selectedConversation && messages.length > 0) {
      markAsRead();
    }
  }, [selectedConversation, messages, markAsRead]);

  // Handle URL parameters for conversation selection
  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    if (conversationId) {
      setSelectedConversation(conversationId);
      // Do not clear the URL param so refresh preserves selection
    }
  }, [searchParams]);

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Link preview cache: messageId -> product preview
  const [linkPreviews, setLinkPreviews] = useState<Record<string, { id: string; image?: string; title?: string }>>({});

  // Detect product links in text messages and fetch preview
  function extractProductIdFromUrl(text: string): string | null {
    try {
      const urlMatch = text.match(/https?:\/\/[^\s]+/);
      const candidate = urlMatch ? urlMatch[0] : '';
      if (!candidate) return null;
      const path = new URL(candidate).pathname; // e.g. /product/abc123
      const m = path.match(/\/(product|listing)\/([^\/?#]+)/);
      return m ? m[2] : null;
    } catch {
      // Fallback: try relative-style matching (user may paste a relative path)
      const m = text.match(/\/(product|listing)\/([^\s]+)/);
      return m ? m[2] : null;
    }
  }

  useEffect(() => {
    // Only look at last ~50 messages to keep it light
    const recent = messages.slice(-50);
    recent.forEach(async (m) => {
      if (m.type !== 'text' || !m.content) return;
      if (linkPreviews[m.id]) return;
      const pid = extractProductIdFromUrl(m.content);
      if (!pid) return;
      try {
        const listing = await getListingById(pid);
        if (listing) {
          setLinkPreviews((prev) => ({
            ...prev,
            [m.id]: { id: listing.id!, image: (listing.images && listing.images[0]) || listing.image, title: listing.title }
          }));
        }
      } catch {}
    });
  }, [messages, linkPreviews]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (recording && mediaRecorderRef.current) {
      stopRecording();
      return;
    }
    if (!newMessage.trim() || !selectedConversation) return;
  
    // Warning gate for risky content
    if (shouldWarnMessage(newMessage)) {
      setPendingSendText(newMessage);
      setShowFraudModal(true);
      return;
    }
  
    await sendMessage(newMessage, 'text', replyToMessage?.id || null);
    setNewMessage('');
    setReplyToMessage(null);
    await setTyping(selectedConversation, false);
    const el = messagesContainerRef.current;
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    } else if (el) {
      el.scrollTop = el.scrollHeight;
    }
  };

  async function startRecording() {
    if (!selectedConversation) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recordingChunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordingChunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(recordingChunksRef.current, { type: mimeType || 'audio/webm' });
        await sendAudioBlob(blob);
        cleanupRecording();
      };
      mediaRecorderRef.current = mr;

      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      const tick = () => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / bufferLength;
        setRecordingLevel(avg);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();

      setRecordingSeconds(0);
      recordingIntervalRef.current = window.setInterval(() => {
        setRecordingSeconds((s) => s + 1);
      }, 1000);

      mr.start();
      setRecording(true);
    } catch (err) {
      console.error('Error starting recording', err);
      // Clean up if recording fails
      cleanupRecording(true);
    }
  }

  function stopRecording() {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  }

  function cancelRecording() {
    setRecording(false);
    cleanupRecording(true);
  }

  async function sendAudioBlob(blob: Blob) {
    const user = auth.currentUser;
    if (!user || !selectedConversation) return;
    const file = new File([blob], `voice-note-${Date.now()}.webm`, { type: blob.type || 'audio/webm' });
    try {
      const url = await uploadMessageAttachment(file, user.uid, selectedConversation);
      await sendMessage(url, 'audio', replyToMessage?.id || null);
      setReplyToMessage(null);
    } catch (err) {
      console.error('Failed to upload/send audio', err);
    }
  }

  function cleanupRecording(discardOnly: boolean = false) {
    try {
      mediaRecorderRef.current = null;
      recordingChunksRef.current = [];
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      if (audioCtxRef.current) {
        audioCtxRef.current.close();
        audioCtxRef.current = null;
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((t) => t.stop());
        audioStreamRef.current = null;
      }
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      setRecordingSeconds(0);
      setRecordingLevel(0);
      if (discardOnly) return;
    } catch {}
  }

  async function handleImageSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const user = auth.currentUser;
    if (!user || !selectedConversation) return;
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    for (const file of files) {
      try {
        const url = await uploadMessageAttachment(file, user.uid, selectedConversation);
        await sendMessage(url, 'image', replyToMessage?.id || null);
      } catch (err) {
        console.error('Failed to upload/send image', err);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
    setReplyToMessage(null);
  }

  const formatMessageTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatConversationTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // Less than a week
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const formatDayLabel = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const today = new Date();
    today.setHours(0,0,0,0);
    const msgDay = new Date(date);
    msgDay.setHours(0,0,0,0);
    const diffDays = Math.round((today.getTime() - msgDay.getTime()) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getOtherParticipant = (conversation: Conversation) => {
    const otherParticipantId = conversation.participants.find(id => id !== currentUser?.uid);
    return otherParticipantId ? conversation.participantDetails[otherParticipantId] : null;
  };

  const filteredConversations = conversations.filter(conversation => {
    if (!searchTerm) return true;
    const otherParticipant = getOtherParticipant(conversation);
    return (
      otherParticipant?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conversation.listingTitle?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const selectedConversationData = conversations.find(c => c.id === selectedConversation);
  const otherParticipant = selectedConversationData ? getOtherParticipant(selectedConversationData) : null;
  const otherParticipantId2 = selectedConversationData ? selectedConversationData.participants.find(id => id !== currentUser?.uid) || null : null;
  const { presence: otherPresence } = usePresence(otherParticipantId2);
  const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : null;

  // Fetch the live listing for the pinned "About this product" card. Keyed on the
  // listing id so it only refetches when the open conversation's product changes.
  const conversationListingId = selectedConversationData?.listingId;
  const [productPreview, setProductPreview] = useState<Listing | null>(null);
  useEffect(() => {
    if (!conversationListingId) { setProductPreview(null); return; }
    let active = true;
    setProductPreview(null);
    getListingById(conversationListingId)
      .then((l) => { if (active) setProductPreview(l); })
      .catch(() => { if (active) setProductPreview(null); });
    return () => { active = false; };
  }, [conversationListingId]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CampusHeader />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CampusHeader />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Please log in to view messages</h2>
            <p className="text-gray-600">You need to be logged in to access your messages.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CampusHeader />
      {showFraudBanner && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start">
            <i className="ri-error-warning-line text-yellow-600 text-lg mr-2 mt-0.5"></i>
            <p className="text-yellow-800 text-sm">
              Please don't pay sellers outside the website. We won't be responsible in case of fraud.
            </p>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 md:py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden h-[90dvh] md:h-[600px] flex">
          
          {/* Conversations List */}
          <div className={`${isMobileView && selectedConversation ? 'hidden' : 'flex'} flex-col w-full md:w-1/3 border-r border-gray-200`}>
            {/* Header */}
            <div className="p-3 md:p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-bold text-gray-900">Messages</h1>
              </div>
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Conversations */}
            <div className="flex-1 overflow-y-auto">
              {conversationsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {searchTerm ? 'No conversations found' : 'No messages yet'}
                </div>
              ) : (
                filteredConversations.map((conversation) => {
                  const participant = getOtherParticipant(conversation);
                  const unreadCount = conversation.unreadCount[currentUser.uid] || 0;
                  
                  return (
                    <div
                      key={conversation.id}
                      onClick={() => setSelectedConversation(conversation.id)}
                      className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedConversation === conversation.id ? 'bg-blue-50 border-blue-200' : ''
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        {/* Avatar */}
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                          {participant?.name.charAt(0).toUpperCase() || 'U'}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-medium text-gray-900 truncate">
                              {participant?.name || 'Unknown User'}
                            </h3>
                            <div className="flex items-center space-x-2">
                              {conversation.lastMessage && (
                                <span className="text-xs text-gray-500">
                                  {formatConversationTime(conversation.lastMessage.timestamp)}
                                </span>
                              )}
                              {unreadCount > 0 && (
                                <span className="bg-blue-600 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                                  {unreadCount}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {conversation.listingTitle && (
                            <p className="text-xs text-blue-600 mb-1">
                              Re: {conversation.listingTitle}
                            </p>
                          )}
                          
                          {conversation.lastMessage && (() => {
                            const lm: any = conversation.lastMessage;
                            const content: string = lm.content || '';
                            const looksLikeAudioUrl = /\.(webm|mp3|m4a|wav|ogg)(\?|$)/i.test(content) || /vercel-storage\.com|\.blob\.|blob\.vercel/i.test(content);
                            const looksLikeImageUrl = /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(content);
                            const type = lm.type || (looksLikeAudioUrl ? 'audio' : looksLikeImageUrl ? 'image' : 'text');
                            let preview: React.ReactNode = content;
                            if (type === 'audio') preview = <AudioPreview src={content} />;
                            else if (type === 'image') preview = <span className="inline-flex items-center gap-1"><ImageIcon size={14} /> Photo</span>;
                            return (
                              <p className="text-sm text-gray-600 truncate">{preview}</p>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className={`${isMobileView && !selectedConversation ? 'hidden' : 'flex'} flex-col flex-1 min-h-0 w-full`}>
            {selectedConversation && otherParticipant ? (
              <>
                {/* Chat Header - Fixed Responsiveness */}
                <div className="p-3 md:p-4 border-b border-gray-200 bg-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 md:space-x-3 min-w-0 flex-1">
                      {isMobileView && (
                        <button
                          onClick={() => setSelectedConversation(null)}
                          className="flex-shrink-0 p-1 hover:bg-gray-100 rounded-full"
                        >
                          <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                      )}
                      
                      <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm md:text-base">
                        {otherParticipant.name.charAt(0).toUpperCase()}
                      </div>
                      
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1 md:gap-2 flex-wrap">
                          <button
                            type="button"
                            className="text-base md:text-lg font-semibold text-gray-900 truncate max-w-[150px] md:max-w-none hover:underline"
                            onClick={async () => {
                              try {
                                const id = (selectedConversationData?.participants || []).find(id => id !== currentUser?.uid);
                                if (id) {
                                  const profile = await getUserProfile(id);
                                  setOtherProfile(profile);
                                }
                              } catch {}
                              setShowProfileModal(true);
                            }}
                          >
                            {otherParticipant.name}
                          </button>
                          <span className={`inline-flex items-center text-xs ${otherPresence?.online ? 'text-green-600' : 'text-gray-500'} flex-shrink-0`}>
                            <span className={`w-2 h-2 rounded-full ${otherPresence?.online ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                            <span className="ml-1 hidden xs:inline">{otherPresence?.online ? 'Online' : 'Offline'}</span>
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
                      <button className="p-1 md:p-2 hover:bg-gray-100 rounded-full">
                        <Info className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Pinned "About this product" card — tap to open the product page */}
                {selectedConversationData?.listingId && (
                  <Link
                    to={`/product/${selectedConversationData.listingId}`}
                    className="flex items-center gap-3 border-b border-gray-200 bg-teal-50/70 px-3 py-2 hover:bg-teal-50 transition-colors"
                  >
                    <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100 ring-1 ring-gray-200">
                      {(productPreview?.images?.[0] || (productPreview as any)?.image) ? (
                        <img
                          src={productPreview?.images?.[0] || (productPreview as any)?.image}
                          alt={productPreview?.title || 'Product'}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-gray-300">
                          <ImageIcon size={18} />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-teal-700">About this product</p>
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {productPreview?.title || selectedConversationData.listingTitle || 'View product'}
                      </p>
                      {productPreview?.price != null && (
                        <p className="text-xs font-bold text-teal-700">{formatNaira(productPreview.price)}</p>
                      )}
                    </div>
                    <ArrowRight className="h-4 w-4 flex-shrink-0 text-teal-600" />
                  </Link>
                )}

                {/* Messages - Fixed Container Responsiveness */}
                <div 
                  className="flex-1 overflow-y-auto p-2 md:p-4 pb-20 md:pb-28 space-y-3 bg-gray-50 w-full" 
                  ref={messagesContainerRef}
                  style={{ maxWidth: '100%', overflowX: 'hidden' }}
                >
                  {messagesLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <p>No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    (() => {
                      const blocks: Array<{ type: 'separator' | 'message'; label?: string; message?: Message }> = [];
                      let lastLabel: string | null = null;
                      messages.forEach((m) => {
                        const label = formatDayLabel(m.timestamp);
                        if (label !== lastLabel) {
                          blocks.push({ type: 'separator', label });
                          lastLabel = label;
                        }
                        blocks.push({ type: 'message', message: m });
                      });
                      return blocks.map((block) => {
                        if (block.type === 'separator') {
                          return (
                            <div key={`sep-${block.label}`} className="flex items-center justify-center my-2">
                              <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full border">{block.label}</span>
                            </div>
                          );
                        }
                        const message = block.message!;
                        const isOwn = message.senderId === currentUser.uid;
                        const repliedMsg = message.replyToId ? messages.find(m => m.id === message.replyToId) : null;
                        return (
                          <div
                            key={message.id}
                            className={`flex ${isOwn ? 'justify-end' : 'justify-start'} w-full px-1`}
                          >
                            <div
                              className={`max-w-[88vw] sm:max-w-[80%] md:max-w-[70%] w-auto px-3 py-2 rounded-lg ${
                                 isOwn
                                   ? 'bg-blue-600 text-white'
                                   : 'bg-white text-gray-900 border border-gray-200'
                               } ${replyToMessage?.id === message.id ? 'ring-2 ring-blue-400' : 'cursor-pointer hover:shadow-md'}`}
                               style={{ 
                                 wordWrap: 'break-word',
                                 overflowWrap: 'break-word'
                               }}
                               onClick={() => {
                                 if (replyToMessage?.id === message.id) {
                                   setReplyToMessage(null);
                                 } else {
                                   setReplyToMessage(message);
                                   setTimeout(() => {
                                     const input = document.querySelector('input["text"]') as HTMLInputElement;
                                     input?.focus();
                                   }, 100);
                                 }
                               }}
                             >
                              {repliedMsg && (
                                <div className={`mb-2 border-l-2 pl-2 text-xs ${isOwn ? 'border-blue-300 text-blue-100' : 'border-gray-300 text-gray-600'} break-words`}>
                                  Replying to: {repliedMsg.type === 'image' ? 'Image' : repliedMsg.type === 'audio' ? 'Voice Note' : (repliedMsg.content?.slice(0, 120) || 'Message')}
                                </div>
                              )}
                              {(() => {
                                const c = message.content || '';
                                const isAudioLegacy = message.type !== 'image' && message.type !== 'audio' && (/\.(webm|mp3|m4a|wav|ogg)(\?|$)/i.test(c) || /vercel-storage\.com|\.blob\.|blob\.vercel/i.test(c));
                                return isAudioLegacy;
                              })() ? (
                                <audio
                                  src={message.content}
                                  controls
                                  className="w-full max-w-full"
                                />
                              ) : message.type === 'image' ? (
                                <img
                                  src={message.content}
                                  alt="Image"
                                  className="w-full max-h-[36vh] object-contain rounded max-w-full cursor-pointer hover:opacity-90"
                                  onClick={() => setImagePreviewMessage(message)}
                                />
                              ) : message.type === 'audio' ? (
                                <audio
                                  src={message.content}
                                  controls
                                  className="w-full max-w-full"
                                />
                              ) : (
                                <>
                                  <p 
                                    className="text-sm break-words whitespace-pre-wrap max-w-full"
                                    style={{ 
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {message.content}
                                  </p>
                                  {linkPreviews[message.id] && (
                                    <a 
                                      href={`/product/${linkPreviews[message.id].id}`} 
                                      className={`block mt-2 rounded ${isOwn ? 'bg-blue-500/20' : 'bg-gray-50'} border ${isOwn ? 'border-blue-300/40' : 'border-gray-200'} overflow-hidden w-full max-w-full`}
                                    >
                                      {linkPreviews[message.id].image && (
                                        <img 
                                          src={linkPreviews[message.id].image!} 
                                          alt={linkPreviews[message.id].title || 'Product'} 
                                          className="w-full h-24 md:h-28 object-cover" 
                                        />
                                      )}
                                      <div className="p-2">
                                        <p className={`text-xs ${isOwn ? 'text-blue-100' : 'text-gray-700'}`}>Check out this product on Campus Market</p>
                                        <p className={`text-xs font-medium truncate ${isOwn ? 'text-white' : 'text-gray-900'}`}>{linkPreviews[message.id].title}</p>
                                      </div>
                                    </a>
                                  )}
                                </>
                              )}
                              <p
                                className={`text-xs mt-1 ${
                                  isOwn ? 'text-blue-100' : 'text-gray-500'
                                }`}
                              >
                                {formatMessageTime(message.timestamp)}
                              </p>
                              {isOwn && (
                                <p className={`text-[10px] mt-1 ${isOwn ? 'text-blue-100' : 'text-gray-500'}`}>
                                  {sending && lastMessageId === message.id ? 'Sending…' : (message.read ? 'Read' : 'Sent')}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()
                  )}
                  <div ref={messagesEndRef} />
                  {otherPresence?.typing && otherPresence?.typingConversationId === selectedConversation && (
                    <div className="text-xs text-gray-500 italic px-2">{otherParticipant.name} is typing… ✍️</div>
                  )}
                 </div>

                 {/* Message Input - Fixed Responsiveness */}
                 <div className="p-3 border-t border-gray-200 bg-white sticky bottom-0 z-10 w-full">
                  {replyToMessage && (
                    <div className="flex items-center justify-between mb-2 px-3 py-2 bg-gray-100 rounded w-full">
                      <div className="text-xs text-gray-700 truncate flex-1 flex items-center gap-2 min-w-0">
                        {replyToMessage.type === 'image' && (
                          <img src={replyToMessage.content} alt="Image" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                        )}
                        <span className="truncate">Replying to: {replyToMessage.type === 'image' ? 'Image' : replyToMessage.type === 'audio' ? 'Voice Note' : (replyToMessage.content?.slice(0, 120) || 'Message')}</span>
                      </div>
                      <button className="text-xs text-gray-500 hover:text-gray-700 flex-shrink-0 ml-2" onClick={() => setReplyToMessage(null)}>Cancel</button>
                    </div>
                  )}
                  <form onSubmit={handleSendMessage} className="flex items-center space-x-2 w-full">
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="p-2 rounded hover:bg-gray-100 flex-shrink-0" title="Attach image">
                      <ImageIcon className="w-5 h-5 text-gray-600" />
                    </button>
                    <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageSelected} />

                    {recording ? (
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <div className="flex items-center px-3 py-2 rounded bg-red-50 border border-red-200 flex-1 min-w-0">
                          <Mic className="w-4 h-4 text-red-600 mr-2 animate-pulse flex-shrink-0" />
                          <div className="w-16 h-4 bg-red-100 rounded overflow-hidden flex-1 min-w-0">
                            <div className="h-4 bg-red-500 transition-all" style={{ width: `${Math.min(100, Math.max(10, (recordingLevel / 255) * 100))}%` }}></div>
                          </div>
                          <span className="ml-2 text-xs text-red-700 whitespace-nowrap flex-shrink-0">Recording {Math.floor(recordingSeconds / 60)}:{String(recordingSeconds % 60).padStart(2, '0')}</span>
                        </div>
                        <button type="button" onClick={stopRecording} className="p-2 rounded bg-red-600 text-white hover:bg-red-700 flex-shrink-0" title="Stop & send">
                          <Square className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={cancelRecording} className="p-2 rounded hover:bg-gray-100 text-xs flex-shrink-0 whitespace-nowrap" title="Cancel">
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={startRecording} className="p-2 rounded hover:bg-gray-100 flex-shrink-0" title="Record voice note">
                        <Mic className="w-5 h-5 text-gray-600" />
                      </button>
                    )}

                    <input
                      type="text"
                      className="flex-1 min-w-0 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Type a message"
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        if (selectedConversation) setTypingState(e.target.value.length > 0, selectedConversation);
                      }}
                    />
                    <button 
                      type="submit" 
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 md:px-4 py-2 rounded flex items-center flex-shrink-0 whitespace-nowrap"
                    >
                      <Send className="w-4 h-4 mr-1" /> 
                      <span className="hidden xs:inline">Send</span>
                    </button>
                  </form>
                </div>
              </>
            ) : (
                   <div className="flex-1 flex items-center justify-center">
                     <div className="text-center p-4">
                       <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                         <Send className="w-8 h-8 text-gray-400" />
                       </div>
                       <h3 className="text-lg font-medium text-gray-900 mb-2">
                         Select a conversation
                       </h3>
                       <p className="text-gray-600">
                         Choose a conversation from the list to start messaging
                       </p>
                     </div>
                   </div>
               )}
          </div>
        </div>
      </div>
      
      <Footer />
      <CallModal
        open={showCallModal}
        onClose={() => {
          setShowCallModal(false);
          setActiveCallId(undefined);
          setIsCaller(true);
        }}
        callerId={currentUser?.uid || ''}
        calleeId={calleeIdForCall || otherParticipantId2 || ''}
        type={callType}
        isCaller={isCaller}
        callId={activeCallId}
      />
      
      {/* Image Preview Modal */}
      {imagePreviewMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4" onClick={() => setImagePreviewMessage(null)}>
          <div className="relative max-w-4xl max-h-full" onClick={(e) => e.stopPropagation()}>
            <button className="absolute -top-12 right-0 text-white hover:text-gray-300 text-2xl" onClick={() => setImagePreviewMessage(null)}>&times;</button>
            <img src={imagePreviewMessage.content} alt="Preview" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
            <div className="mt-2 text-white text-center text-sm">
              Sent {formatMessageTime(imagePreviewMessage.timestamp)}
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowProfileModal(false)}>
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center text-gray-600">
                {otherProfile?.photoURL ? (
                  <img src={otherProfile.photoURL} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-semibold">{(otherParticipant?.name || 'U').charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-lg font-semibold text-gray-900 truncate">{otherProfile?.fullName || otherParticipant?.name}</p>
                {otherProfile?.university && (
                  <p className="text-sm text-gray-600 truncate">{otherProfile?.university}</p>
                )}
              </div>
            </div>
            {otherProfile?.bio && (
              <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap break-words">{otherProfile.bio}</p>
            )}
            <div className="grid grid-cols-2 gap-3 text-sm">
              {otherProfile?.phone && (
                <div className="text-gray-700"><span className="font-medium">Phone:</span> {otherProfile.phone}</div>
              )}
              {otherProfile?.email && (
                <div className="text-gray-700"><span className="font-medium">Email:</span> {otherProfile.email}</div>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Link to={`/report?type=user&id=${otherProfile?.uid || otherParticipantId2 || ''}&name=${encodeURIComponent(otherProfile?.fullName || otherParticipant?.name || 'Unknown User')}`} className="px-3 py-2 rounded border border-red-300 text-red-600 hover:bg-red-50">Report User</Link>
              <button className="px-3 py-2 rounded border border-gray-300" onClick={() => setShowProfileModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;